import * as React from 'react';
import bind from 'bind-decorator';
import messages from './displayMessages';
import uiMessages from 'ui/components/displayMessages';
import { connect } from 'react-redux';
import { DashboardNavigationLinks, TabbedPageLayout } from 'dashboard';
import { DiscussionsApi } from 'global/api';
import { getLoggedInStatus, getUsername } from 'auth/selectors';
import { intl } from 'i18n';
import { MessageComposeForm, MessageSearchBox, MessagePostItem, MessageTopicItem } from '../../components';
import { RegistrationGate } from 'ui/components/RegistrationGate';
import { RootState } from 'global/state';
import { RouteComponentProps } from 'react-router';
import { LxConfig, ROUTES } from 'global/constants';
import { showErrorMessage } from 'ui/components/GlobalMessageReporter/dispatch';
import { UI_IS_SM } from 'ui/breakpoints';
import { WrappedMessage } from 'utils';
import { HeadingFour } from 'elements/components/Text';
import * as moment from 'moment-shortformat';
import update from 'immutability-helper';
import { UserAvatar } from 'user/components';

import {
    Dropdown,
    Icon,
    Modal,
    UnderConstruction,
} from 'ui/components';

import {
    Conversation,
    ConversationInfo,
    ConversationInfoStatusEnum,
    ConversationStatus,
    ConversationWithMessages,
    ConversationStatusReasonEnum,
    DiscussionAuthor,
    DiscussionsMessageCreate,
} from 'labxchange-client';

enum ModalType {
    None,
    AskBlock,
    AskUnblock,
}

interface InternalProps extends ReduxStateProps, ContainerState {
    newConversationRecipient?: DiscussionAuthor;
    conversationsInfos: ConversationInfo[];
    selectConversation: (conversationId: string) => void;
    deSelectConversation: () => void;
    sendMessage: (data: DiscussionsMessageCreate) => void;
    onBlockUser: (username: string) => void;
    onUnblockUser: (username: string) => void;
    onAcceptPending: (username: string) => void;
    conversationStatus?: ConversationStatus;
}

interface InternalState {
    isMobileView: boolean;
    autoScroll: boolean;
    currentModal?: React.ReactNode;
}

class MessagePageInternal extends React.PureComponent<InternalProps, InternalState> {
    private messageListRef: React.RefObject<HTMLDivElement>;
    private mediaQuery = UI_IS_SM;

    constructor(props: any) {
        super(props);

        this.messageListRef = React.createRef();
        this.state = {
            isMobileView: false,
            autoScroll: false,
            currentModal: ModalType.None,
        };
    }

    @bind private setIsMobileView(): void {
        this.setState({ isMobileView: this.mediaQuery.matches });
    }

    public componentDidMount() {
        this.mediaQuery.addListener(this.setIsMobileView);
        this.setIsMobileView();
        if (!this.props.loading) {
            if (!this.props.currentConversation && !this.props.newConversationRecipient) {
                showErrorMessage(<WrappedMessage message={messages.messagesFailedLoading} />);
            }
        }
    }

    public componentWillUnmount() {
        this.mediaQuery.removeListener(this.setIsMobileView);
    }

    public componentDidUpdate() {
        if (this.props.loading || this.props.sending) {
            this.setState({ autoScroll: true });
        } else if (this.state.autoScroll) {
            const messageList = this.messageListRef.current;
            if (messageList) {
                messageList.scrollTop = messageList.scrollHeight;
            }
            this.setState({ autoScroll: false });
        }
    }

    public render() {
        const { conversations, conversationStatus, currentConversation, newConversationRecipient, loading, sending } = this.props;
        const isMobileView = this.state.isMobileView;
        let conversationRecipient;
        if (newConversationRecipient) {
            conversationRecipient = newConversationRecipient;
        } else if (currentConversation) {
            conversationRecipient = currentConversation.conversation.recipient;
        }

        let currentConversationInfo: ConversationInfo | undefined;
        if (conversationRecipient !== undefined) {
            currentConversationInfo = this.getConversationInfo(conversationRecipient);
        }

        let isDisabled = true;
        if (currentConversation && currentConversation.status) {
            isDisabled = !currentConversation.status.allowed;
        } else if (conversationStatus) {
            isDisabled = !conversationStatus.allowed;
        }

        return (
            <TabbedPageLayout pageTitle={messages.pageTitle} tabs={DashboardNavigationLinks}>
                <div className={`message-page row ${loading ? 'message-page-loading' : 'message-page-ready'}`}>
                    <div className={`inbox-wrapper col-12 ${isMobileView ? 'mobile' : ''}`}>
                        <div className='inbox white-box'>
                            <div className='inbox-top row'>
                                {(!isMobileView || !currentConversation) &&
                                    <div className={`inbox-search col-4 ${isMobileView ? 'mobile col-12' : ''}`}>
                                        <form>
                                            <MessageSearchBox />
                                        </form>
                                    </div>
                                }
                                {(!isMobileView || currentConversation) &&
                                    <div className={`message-party col-8 ${isMobileView ? 'mobile col-12' : ''}`}>
                                        {(isMobileView && currentConversation) &&
                                            <button
                                                className='close-message-pane'
                                                onClick={this.props.deSelectConversation}>
                                                <Icon name='chevron-left' zoom='1.5em' />
                                            </button>
                                        }
                                        <div className='message-header'>
                                            {currentConversation && conversationRecipient &&
                                                <h2>{conversationRecipient.fullName}</h2>
                                            }
                                            <p>{currentConversation && conversationRecipient && (
                                                conversationRecipient.jobTitle ?
                                                    conversationRecipient.company ?
                                                        <WrappedMessage
                                                            message={messages.messagesAddresseeProfession}
                                                            values={{
                                                                jobTitle: conversationRecipient.jobTitle,
                                                                company: conversationRecipient.company,
                                                            }}
                                                        />
                                                        : conversationRecipient.jobTitle
                                                    : conversationRecipient.company
                                            )}</p>
                                        </div>
                                        {currentConversationInfo &&
                                            <Dropdown id='message-page-menu'
                                                title={intl.formatMessage(messages.messageActionsMenu)}
                                                label={<Icon fill='#003e6b' name='kebab-vertical' zoom='24' />}
                                            >
                                                <ul>
                                                    {!currentConversationInfo!.isBlocked &&
                                                        <li>
                                                            <button onClick={this.askBlockAddressee}>
                                                                <WrappedMessage message={messages.blockButton} />
                                                            </button>
                                                        </li>
                                                    }
                                                    {currentConversationInfo!.isBlocked &&
                                                        <li>
                                                            <button onClick={this.askUnblockAddressee}>
                                                                <WrappedMessage message={messages.unblockButton} />
                                                            </button>
                                                        </li>
                                                    }
                                                    <li>
                                                        <UnderConstruction>
                                                            <button>
                                                                <WrappedMessage message={messages.reportButton} />
                                                            </button>
                                                        </UnderConstruction>
                                                    </li>
                                                </ul>
                                            </Dropdown>
                                        }
                                    </div>
                                }
                            </div>
                            <div className='inbox-bottom row'>
                                {(!isMobileView || !currentConversation) &&
                                    <div className={`topic-list col-4 ${isMobileView ? 'mobile col-12' : ''}`}>
                                        <div className='list-group list-group-flush'>
                                            {newConversationRecipient && conversationRecipient &&
                                                <MessageTopicItem
                                                    key={-1}
                                                    conversation={{
                                                        id: `new-${newConversationRecipient.username}`,
                                                        updated: new Date(),
                                                        created: new Date(),
                                                        unread: 0,
                                                        latestReply: '',
                                                        author: newConversationRecipient,
                                                        recipient: newConversationRecipient,
                                                        latestReplyUpdated: new Date(),
                                                    }}
                                                    user={conversationRecipient}
                                                    info={currentConversationInfo}
                                                    selected={true}
                                                />
                                            }
                                            {conversations &&
                                                conversations.slice().map((conversation) => {
                                                    const recipient = conversation.recipient;
                                                    const isSelected = this.isConversationSelected(conversation.id);
                                                    return (
                                                        <MessageTopicItem
                                                            key={conversation.id}
                                                            conversation={conversation}
                                                            user={recipient}
                                                            info={recipient && this.getConversationInfo(recipient)}
                                                            selected={isSelected}
                                                            selectConversation={this.props.selectConversation}
                                                        />
                                                    );
                                                })
                                            }
                                        </div>
                                    </div>
                                }
                                {(!isMobileView || currentConversation) &&
                                    <div className={`message-pane col-8 ${isMobileView ? 'mobile col-12' : ''}`}>
                                        <div
                                            className={`messages ${loading ? 'spinner' : ''}`}
                                            ref={this.messageListRef}>
                                            {!loading ?
                                                conversations?.length ?
                                                    <div className='list-group list-group-flush'>
                                                        {this.renderCurrentConversation()}
                                                    </div> :
                                                    <div className='empty-messages'>
                                                        <div className='empty-message-header'>
                                                            <WrappedMessage message={messages.messagesEmpty} />
                                                        </div>
                                                        <div className='empty-message-description'>
                                                            <WrappedMessage
                                                                message={messages.messagesEmptyDescription} />
                                                        </div>
                                                    </div>
                                                : null}
                                        </div>
                                        {conversationRecipient &&
                                            <MessageComposeForm
                                                conversation={currentConversation}
                                                recipient={conversationRecipient}
                                                sending={sending}
                                                sendMessage={this.props.sendMessage}
                                                disabled={isDisabled}
                                            />
                                        }
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                {this.renderModals()}
            </TabbedPageLayout>
        );
    }

    private renderModals(): React.ReactNode | null {
        const user = this.props.currentConversation?.conversation.recipient;
        if (user === undefined) { return null; }
        switch (this.state.currentModal) {
            case ModalType.AskBlock: {
                return (
                    <Modal
                        contentScrolls={false}
                        onRequestClose={this.onCloseModal}
                        showBackButton={false}
                        showTopBar={false}
                        className='messages-block-modal'
                        content={
                            <>
                                <UserAvatar username={user.username} />
                                <div className='block-question'>
                                    <WrappedMessage
                                        message={messages.askBlockUser}
                                        values={{ name: user.fullName }}
                                    />
                                </div>
                                <div className='block-info-msg'>
                                    <WrappedMessage
                                        message={messages.blockUserInfo}
                                        values={{ name: user.fullName }}
                                    />
                                </div>
                                <div>
                                    <button
                                        className='block-btn btn secondary-button btn-outline-primary'
                                        onClick={this.onBlockAddressee}
                                    >
                                        <WrappedMessage message={messages.blockButton} />
                                    </button>
                                    <button className='primary-button' onClick={this.onCloseModal}>
                                        <WrappedMessage message={messages.cancelBlockButton} />
                                    </button>
                                </div>
                            </>
                        }
                    />
                );
            }

            case ModalType.AskUnblock: {
                return (
                    <Modal
                        contentScrolls={false}
                        onRequestClose={this.onCloseModal}
                        showBackButton={false}
                        showTopBar={false}
                        className='messages-block-modal'
                        content={
                            <>
                                <UserAvatar username={user.username} />
                                <div className='block-question'>
                                    <WrappedMessage
                                        message={messages.askUnblockUser}
                                        values={{ name: user.fullName }}
                                    />
                                </div>
                                <div className='block-info-msg'>
                                    <WrappedMessage
                                        message={messages.unblockUserInfo}
                                        values={{ name: user.fullName }}
                                    />
                                </div>
                                <div>
                                    <button
                                        className='block-btn btn secondary-button btn-outline-primary'
                                        onClick={this.onCloseModal}
                                    >
                                        <WrappedMessage message={messages.cancelUnblockButton} />
                                    </button>
                                    <button className='primary-button' onClick={this.onUnblockAddressee}>
                                        <WrappedMessage message={messages.unblockButton} />
                                    </button>
                                </div>
                            </>
                        }
                    />
                );
            }
            case ModalType.None: default: {
                return null;
            }
        }
    }

    private renderCurrentConversation(): React.ReactNode | null {
        const currentConversation = this.props.currentConversation;
        if (!currentConversation) { return null; }

        const currentRecipient = currentConversation.conversation.recipient;
        let currentConversationInfo: ConversationInfo | undefined;
        if (currentRecipient !== undefined || !currentRecipient) {
            currentConversationInfo = this.getConversationInfo(currentRecipient);
        } else {
            return null;
        }

        if (currentConversationInfo && currentConversationInfo.status === ConversationInfoStatusEnum.PendingYou
            && !currentConversationInfo.isBlocked && !currentConversationInfo.hasBlockedYou
        ) {
            return (
                <div className='message-pending-box'>
                    <HeadingFour
                        level={3}
                        titleDescriptor={messages.messageRequest}
                        titleDescriptorValues={{ name: currentRecipient.fullName }}
                    />
                    <div className='message-pending-inner'>
                        {currentConversation.messages.slice().map((message, i) => (
                            <p
                                key={message.id}
                                className='message-post-content'
                                dangerouslySetInnerHTML={{ __html: message.content }}
                            />
                        ))}
                        <p className='signature'>{currentRecipient.fullName}</p>
                    </div>
                    <p>
                        <WrappedMessage message={messages.acceptMessageInvitation}
                            values={{ name: currentRecipient.fullName }} />
                    </p>
                    <button
                        className='block-btn btn secondary-button btn-outline-primary'
                        onClick={this.askBlockAddressee}
                    >
                        <WrappedMessage message={messages.declineButton} />
                    </button>
                    <button
                        className='primary-button'
                        onClick={this.onAcceptPendingAddressee}
                    >
                        <WrappedMessage message={messages.acceptButton} />
                    </button>
                </div>
            );
        } else {
            const conversationMessages = currentConversation.messages.slice().map((message, i) => {
                let sameUser = false;
                let sameDay = false;
                if (i > 0) {
                    const previousMessage = currentConversation.messages[i - 1];
                    const postDay = moment(message.created).format('YYYY-MM-DD');
                    const prevPostDay = moment(previousMessage.created).format('YYYY-MM-DD');
                    sameDay = postDay === prevPostDay;
                    sameUser = sameDay && (message.author === previousMessage.author);
                }
                return (
                    <MessagePostItem
                        key={message.id}
                        message={message}
                        user={this.findUser(message.author)}
                        sameDay={sameDay}
                        sameUser={sameUser}
                        loggedInUsername={this.props.loggedInUsername}
                    />
                );
            });

            let alertBox = null;
            if (currentConversationInfo && currentConversationInfo.isBlocked) {
                alertBox = (
                    <div className='message-warning'>
                        <WrappedMessage message={messages.youBlockedThem}
                            values={{ name: currentRecipient.fullName }}
                        />
                        {' '}
                        <button className='link-button' onClick={this.onUnblockAddressee}>
                            <WrappedMessage message={messages.undo} />{'.'}
                        </button>
                    </div>
                );
            } else if (currentConversationInfo && currentConversationInfo.hasBlockedYou) {
                alertBox = (
                    <div className='message-info'>
                        <WrappedMessage message={messages.theyBlockedYou} />
                    </div>
                );
            } else if (currentConversationInfo && currentConversationInfo.status === ConversationInfoStatusEnum.PendingThem) {
                alertBox = (
                    <div className='message-info'>
                        <WrappedMessage message={messages.yourMessagePending} />
                    </div>
                );
            }

            return (
                <>
                    {conversationMessages}
                    {alertBox}
                </>
            );
        }
    }

    private findUser(username: string): DiscussionAuthor | undefined {
        if (username === undefined) { return undefined; }

        let user;
        if (this.props.currentConversation) {
            user = this.props.currentConversation.participants.find((u) => {
                return u.username === username;
            });
        }

        return user;
    }

    private isConversationSelected(conversationId: string) {
        const currentConversation = this.props.currentConversation;
        return (currentConversation && (currentConversation.conversation.id === conversationId)) || false;
    }

    private getConversationInfo(author: DiscussionAuthor): ConversationInfo | undefined {
        for (const info of (this.props.conversationsInfos || [])) {
            if (info.username === author.username) {
                return info;
            }
        }
        return;
    }

    @bind private askBlockAddressee() {
        this.setState({
            currentModal: ModalType.AskBlock,
        });
    }

    @bind private askUnblockAddressee() {
        this.setState({
            currentModal: ModalType.AskUnblock,
        });
    }

    @bind private onAcceptPendingAddressee() {
        const user = this.props.currentConversation?.conversation.recipient;
        if (user === undefined) { return; }
        this.props.onAcceptPending(user.username);
    }

    @bind private onBlockAddressee() {
        this.onCloseModal();
        const user = this.props.currentConversation?.conversation.recipient;
        if (user === undefined) { return; }
        this.props.onBlockUser(user.username);
    }

    @bind private onUnblockAddressee() {
        this.onCloseModal();
        const user = this.props.currentConversation?.conversation.recipient;
        if (user === undefined) { return; }
        this.props.onUnblockUser(user.username);
    }

    @bind private onCloseModal() {
        this.setState({
            currentModal: ModalType.None,
        });
    }
}

interface MatchProps {
    conversation?: string;
}

interface ContainerState {
    conversations: Conversation[];
    conversationsInfos: ConversationInfo[];
    conversationStatus?: ConversationStatus;
    currentConversation?: ConversationWithMessages;
    newConversationRecipient?: DiscussionAuthor;
    loading: boolean;
    sending: boolean;
    fetchConversationsTimer?: number;
}

interface ContainerProps extends ReduxStateProps, RouteComponentProps<MatchProps> { }

export class MessagePageContainer extends React.PureComponent<ContainerProps, ContainerState> {
    constructor(props: any) {
        super(props);

        this.state = {
            loading: true,
            sending: false,
            conversations: [],
            conversationsInfos: [],
        };
    }

    public componentDidMount() {
        if (!this.props.isLoggedIn) { return; }
        this.loadData();
    }

    public componentWillUnmount() {
        this.clearFetchConversationsTimer();
    }

    public componentDidUpdate(prevProps: ContainerProps) {
        if (!this.props.isLoggedIn) { return; }

        if (this.props.location.pathname !== prevProps.location.pathname) {
            if (this.props.match.path === ROUTES.Messages.MESSAGE) {
                this.loadCurrentConversation();
            } else {
                this.setState({ currentConversation: undefined, conversationStatus: undefined });
                this.loadData();
            }
        }
    }

    @bind private async setFetchConversationsTimer() {
        this.clearFetchConversationsTimer();
        await this.loadConversations();
        await this.loadCurrentConversation();
        const fetchConversationsTimer = window.setTimeout(this.setFetchConversationsTimer, LxConfig.MessagePollDefaultInterval);
        this.setState({ fetchConversationsTimer });
    }

    public clearFetchConversationsTimer() {
        if (this.state.fetchConversationsTimer) {
            window.clearTimeout(this.state.fetchConversationsTimer);
        }
    }

    public async loadData() {
        this.setState({ loading: true });
        try {
            let newConversationRecipient;
            let conversationStatus;

            await this.loadConversations();

            if (this.isNewConversation()) {
                const params = new URLSearchParams(this.props.location.search);
                const username = params.get('recipient') || '';
                newConversationRecipient = await DiscussionsApi.authorRead({ username });
                const status = await DiscussionsApi.conversationsCheckCanMessage({ id: username });
                conversationStatus = status;
            } else {
                await this.loadCurrentConversation();
            }
            this.setState({
                loading: false,
                conversationStatus,
                newConversationRecipient,
            });
        } catch (err) {
            showErrorMessage(<WrappedMessage message={messages.messagesFailedLoading} />);
        }
        window.setTimeout(this.setFetchConversationsTimer, 5_000);
    }

    public async loadConversations() {
        try {
            const response = await DiscussionsApi.conversationsList({});
            this.setState({
                conversations: response.conversations,
                conversationsInfos: response.conversationsInfos,
            });
        } catch (err) {
            showErrorMessage(<WrappedMessage message={messages.messagesFailedLoading} />);
        }
    }

    @bind public async loadCurrentConversation() {
        const conversationId = this.props.match.params.conversation;
        if (conversationId) {
            const response = await DiscussionsApi.conversationsRead({ id: conversationId });
            const currentConversation = response;
            const conversationStatus = response.status;
            this.setState({ currentConversation, conversationStatus });
        }
    }

    private isNewConversation() {
        return window.location.pathname === ROUTES.Messages.MESSAGE_NEW;
    }

    public render() {
        if (!this.props.isLoggedIn) {
            return <RegistrationGate
                title={intl.formatMessage(messages.notLoggedAlertMessageTitle)}
                body={intl.formatMessage(messages.notLoggedAlertMessageBody)}
                primaryButtonText={uiMessages.uiSignUp}
                secondaryButtonText={uiMessages.uiSignIn}
                image='/assets/images/access.svg'
            />;
        }

        return (
            <MessagePageInternal
                conversations={this.state.conversations}
                conversationsInfos={this.state.conversationsInfos}
                conversationStatus={this.state.conversationStatus}
                currentConversation={this.state.currentConversation}
                deSelectConversation={this.deSelectConversation}
                isLoggedIn={this.props.isLoggedIn}
                loading={this.state.loading}
                loggedInUsername={this.props.loggedInUsername}
                newConversationRecipient={this.state.newConversationRecipient}
                onAcceptPending={this.onAcceptPending}
                onBlockUser={this.onBlockUser}
                onUnblockUser={this.onUnblockUser}
                selectConversation={this.selectConversation}
                sending={this.state.sending}
                sendMessage={this.sendMessage}
            />
        );
    }

    @bind public selectConversation(conversationId: string) {
        this.props.history.push(ROUTES.Messages.MESSAGE_SLUG(conversationId));
    }

    @bind public deSelectConversation() {
        this.props.history.push(ROUTES.Messages.HOME);
    }

    @bind private onAcceptPending(username: string) {
        this.setState({ loading: true }, async () => {
            try {
                await DiscussionsApi.conversationsAcceptPending({ id: username });
            } catch (err) {
                showErrorMessage('Error accepting message request.', { exception: err });
            }

            // Refresh to pull in the latest state (ie. thread now accepted)
            this.loadData();
        });
    }

    @bind private onUnblockUser(username: string) {
        this.setState({ loading: true }, async () => {
            try {
                await DiscussionsApi.conversationsUnblockUser({ id: username });
            } catch (err) {
                showErrorMessage('Error unblocking user.', { exception: err });
            }

            // Refresh to pull in the latest state (ie. user now unblocked)
            this.loadData();
        });
    }

    @bind private onBlockUser(username: string) {
        this.setState({ loading: true }, async () => {
            try {
                await DiscussionsApi.conversationsBlockUser({ id: username });
            } catch (err) {
                showErrorMessage('Error blocking user.', { exception: err });
            }

            // Refresh to pull in the latest state (ie. user blocked)
            this.loadData();
        });
    }

    @bind public async sendMessage(data: DiscussionsMessageCreate) {
        this.setState({ sending: true });
        let newMessage;
        try {
            newMessage = await DiscussionsApi.conversationsCreateMessage({ data });
        } catch (response) {
            this.setState({ sending: false });
            response.json().then((body: { reason?: string }) => {
                let reason: string | undefined;
                /**
                 * Backend messages meant to be human readable but
                 * we maybe still want to have a translations table in frontend.
                 */
                if (body.reason) { reason = body.reason; }
                if (reason) {
                    if (reason === ConversationStatusReasonEnum.SenderMessagesDisabled) {
                        showErrorMessage(
                            <WrappedMessage message={messages.messagesFailedSendingMessageWithReason}
                                values={{ reason: intl.formatMessage(messages.messagesFailedSenderDisabledMessaging) }} />,
                            { confirmText: uiMessages.uiConfirmationButton }
                        );
                    } else if (reason === ConversationStatusReasonEnum.ReceiverMessagesDisabled) {
                        showErrorMessage(
                            <WrappedMessage message={messages.messagesFailedSendingMessageWithReason}
                                values={{ reason: intl.formatMessage(messages.messagesFailedReceiverDisabledMessaging) }}
                            />,
                            { confirmText: uiMessages.uiConfirmationButton }
                        );
                    } else if (reason === ConversationStatusReasonEnum.SenderBlockedReceiver) {
                        showErrorMessage(
                            <WrappedMessage message={messages.messagesFailedSendingMessageWithReason}
                                values={{ reason: intl.formatMessage(messages.messagesFailedSenderBlocked) }} />,
                            { confirmText: uiMessages.uiConfirmationButton }
                        );
                    } else if (reason === ConversationStatusReasonEnum.ReceiverBlockedSender) {
                        showErrorMessage(
                            <WrappedMessage message={messages.messagesFailedSendingMessageWithReason}
                                values={{ reason: intl.formatMessage(messages.messagesFailedRecipientBlocked) }} />,
                            { confirmText: uiMessages.uiConfirmationButton }
                        );
                    } else {
                        showErrorMessage(
                            <WrappedMessage
                                message={messages.messagesFailedSendingMessageWithReason}
                                values={{ reason }}
                            />, {
                            confirmText: uiMessages.uiConfirmationButton,
                        });
                    }
                } else {
                    showErrorMessage(<WrappedMessage message={messages.messagesFailedSendingMessage} />);
                }
            });
            throw response;
        }

        if (newMessage) {
            if (this.state.currentConversation && this.state.currentConversation.conversation.id === newMessage.conversation) {
                const newMessages = this.state.currentConversation.messages.slice();
                newMessages.push(newMessage.message);
                this.setState({
                    currentConversation: update(this.state.currentConversation!,
                        { messages: { $set: newMessages } },
                    )
                });
            } else {
                this.selectConversation(newMessage.conversation);
                this.setState({ newConversationRecipient: undefined });
                await this.loadData();

            }

            this.setState({ sending: false });
        }
    }
}

interface ReduxStateProps {
    loggedInUsername: string;
    isLoggedIn: boolean;
}

export const MessagePage = connect<ReduxStateProps, {}, RootState>(
    (state: RootState) => ({
        isLoggedIn: getLoggedInStatus(state),
        loggedInUsername: getUsername(state),
    }), {},
)(MessagePageContainer);
